// PWA Service Worker Registration and Management

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('❌ Service Worker desteklenmiyor');
    return null;
  }

  // Skip service worker in development mode due to Vite dev server limitations
  if (import.meta.env.DEV) {
    console.log('⚠️ Service Worker development modda devre dışı (production\'da aktif olacak)');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });

    console.log('✅ Service Worker kaydedildi:', registration.scope);

    // Check for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // Yeni versiyon mevcut!
          console.log('🆕 Yeni versiyon mevcut!');
          
          // Kullanıcıya bildir
          if (confirm('Yeni bir güncelleme mevcut! Yenilemek ister misiniz?')) {
            window.location.reload();
          }
        }
      });
    });

    // Auto-update check (her saat)
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    return registration;
  } catch (error) {
    console.error('❌ Service Worker kaydı başarısız:', error);
    return null;
  }
};

// Unregister (development için)
export const unregisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (let registration of registrations) {
      await registration.unregister();
      console.log('🗑️ Service Worker kaldırıldı');
    }
  }
};

// Check if app is installed
export const isAppInstalled = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
};

// Get install prompt event
export const setupInstallPrompt = (
  callback: (event: BeforeInstallPromptEvent) => void
) => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    callback(e as BeforeInstallPromptEvent);
  });
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.log('❌ Bildirimler desteklenmiyor');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

// Show local notification
export const showNotification = async (
  title: string,
  options?: NotificationOptions
) => {
  if (Notification.permission === 'granted' && 'serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      ...options
    });
  }
};

// Update cache version
export const updateCacheVersion = (newVersion: string) => {
  // This will be used in build process
  console.log('Cache version updated to:', newVersion);
};
